import { useQuery } from '@tanstack/react-query';
import { useBranding } from '@/hooks/useBranding';
import { cn } from '@/lib/cn';
import { fetchDisplay } from './api';
import { LevelBand, minutesLabel } from './ui';

/**
 * The ED board's TV view — `?display=er&branch=<CODE>` (design §7), the same
 * unauthenticated public-surface pattern as the waiting-room StatusBoard.
 * The server sends initials (or "Unknown"), bay, level colour, a coarse
 * status and the clocks — no chief complaint, no MLC flag, no UHID, no id —
 * so the screen is safe on a wall the public can see.
 */
export default function EdDisplayBoard() {
  const branch = new URLSearchParams(window.location.search).get('branch') ?? undefined;
  const { branding } = useBranding();
  const q = useQuery({ queryKey: ['public', 'er-display', branch ?? ''], queryFn: () => fetchDisplay(branch), refetchInterval: 20_000 });

  return (
    <div className="min-h-screen bg-sidebar p-6 text-white">
      <div className="mb-5 flex items-center justify-between">
        <div className="text-[26px] font-bold">{branding.hospital_name} — Emergency</div>
        <div className="text-[18px] opacity-80">{new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
      </div>
      {q.isError && <div className="rounded-lg bg-white/10 p-4">Board unavailable — retrying…</div>}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {(q.data ?? []).map((r) => (
          <div key={r.key} className={cn('flex items-stretch gap-3 rounded-xl bg-white/10 p-3', (r.door_to_doctor_breached || r.retriage_overdue) && 'ring-2 ring-danger')}>
            <LevelBand color={r.level_color} className="w-2.5" />
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="text-[22px] font-bold">{r.name}</span>
                <span className="text-[18px] font-semibold opacity-90">{r.bay ?? ''}</span>
              </div>
              <div className="text-[15px] opacity-90">{r.level_label ?? 'Awaiting triage'} · {r.status}</div>
              <div className="mt-1 font-mono text-[15px]">
                in ED {minutesLabel(r.minutes_since_arrival)}
                {r.boarding_minutes != null && ` · boarding ${minutesLabel(r.boarding_minutes)}`}
                {r.retriage_overdue && ' · re-triage due'}
              </div>
            </div>
          </div>
        ))}
      </div>
      {q.data && q.data.length === 0 && <div className="mt-10 text-center text-[20px] opacity-70">No patients in the Emergency Department</div>}
    </div>
  );
}
